import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Users,
  Bot,
  MessageSquare,
  Sparkles,
  Download,
  Loader2,
} from "lucide-react";
import RefreshButton from "../components/layout/RefreshButton";
import Select from "../components/ui/Select";
import { adminApi } from "../services/adminApi";

const COLORS = [
  "#4F46E5",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
];

const DATE_RANGES = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "1y", label: "Last year" },
];

export default function Analytics() {
  const [dateRange, setDateRange] = useState("30d");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [analytics, setAnalytics] = useState({
    overview: {},
    userGrowth: [],
    agentsByCategory: [],
    requestsByStatus: [],
    feedbackByType: [],
    dailyActivity: [],
    topAgents: [],
  });

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await adminApi.getAnalytics({ range: dateRange });
      setAnalytics(response);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    }
  }, [dateRange]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchAnalytics();
      setIsLoading(false);
    };
    loadData();
  }, [fetchAnalytics]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchAnalytics();
    setIsRefreshing(false);
  };

  const handleExport = async (format) => {
    try {
      setIsExporting(true);
      const blob = await adminApi.exportAnalytics({ range: dateRange, format });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `analytics-${dateRange}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || "0";
  };

  const StatCard = ({ title, value, change, icon: Icon, color }) => (
    <div className="card p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-text-muted text-sm">{title}</p>
          <p className="text-2xl font-bold text-text-primary mt-1">
            {formatNumber(value)}
          </p>
          {change !== undefined && (
            <div
              className={`flex items-center gap-1 mt-2 text-sm ${
                change >= 0 ? "text-success" : "text-danger"
              }`}
            >
              {change >= 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
              <span>{Math.abs(change)}% from last period</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-bg-secondary rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-bg-secondary rounded-xl"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-bg-secondary rounded-xl"></div>
            <div className="h-80 bg-bg-secondary rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Analytics</h1>
          <p className="text-text-secondary mt-1">
            Platform insights and metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="min-w-[160px]">
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              options={DATE_RANGES}
              placeholder="Select range"
            />
          </div>
          <RefreshButton
            onClick={handleRefresh}
            isLoading={isRefreshing}
            lastUpdated={lastUpdated}
          />
          <div className="relative group">
            <button className="btn-secondary" disabled={isExporting}>
              {isExporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {isExporting ? "Exporting..." : "Export"}
            </button>
            {!isExporting && (
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-border py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <button
                  onClick={() => handleExport("csv")}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-bg-secondary"
                >
                  Export as CSV
                </button>
                <button
                  onClick={() => handleExport("json")}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-bg-secondary"
                >
                  Export as JSON
                </button>
                <button
                  onClick={() => handleExport("pdf")}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-bg-secondary"
                >
                  Export as PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Total Users"
          value={analytics.overview?.totalUsers}
          change={analytics.overview?.userGrowth}
          icon={Users}
          color="bg-primary/10 text-primary"
        />
        <StatCard
          title="Total Agents"
          value={analytics.overview?.totalAgents}
          change={analytics.overview?.agentGrowth}
          icon={Bot}
          color="bg-success/10 text-success"
        />
        <StatCard
          title="Custom Requests"
          value={analytics.overview?.totalRequests}
          change={analytics.overview?.requestGrowth}
          icon={Sparkles}
          color="bg-warning/10 text-warning"
        />
        <StatCard
          title="Feedback Items"
          value={analytics.overview?.totalFeedback}
          change={analytics.overview?.feedbackGrowth}
          icon={MessageSquare}
          color="bg-accent/10 text-accent"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* User Growth Chart */}
        <div className="card p-6">
          <h3 className="font-semibold text-text-primary mb-4">User Growth</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={analytics.userGrowth}>
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
              />
              <Area
                type="monotone"
                dataKey="users"
                stroke="#4F46E5"
                fillOpacity={1}
                fill="url(#colorUsers)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Daily Activity Chart */}
        <div className="card p-6">
          <h3 className="font-semibold text-text-primary mb-4">
            Daily Activity
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.dailyActivity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
              />
              <Legend />
              <Bar
                dataKey="sessions"
                fill="#4F46E5"
                name="Sessions"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="messages"
                fill="#10B981"
                name="Messages"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Agents by Category */}
        <div className="card p-6">
          <h3 className="font-semibold text-text-primary mb-4">
            Agents by Category
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={analytics.agentsByCategory}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {analytics.agentsByCategory?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Requests by Status */}
        <div className="card p-6">
          <h3 className="font-semibold text-text-primary mb-4">
            Custom Agent Requests by Status
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={analytics.requestsByStatus}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {analytics.requestsByStatus?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Feedback by Type */}
        <div className="card p-6">
          <h3 className="font-semibold text-text-primary mb-4">
            Feedback by Type
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={analytics.feedbackByType}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {analytics.feedbackByType?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Agents */}
      <div className="card p-6">
        <h3 className="font-semibold text-text-primary mb-4">
          Top Performing Agents
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
                  Agent
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
                  Owner Email
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
                  Category
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-text-muted">
                  Sessions
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-text-muted">
                  Messages
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-text-muted">
                  Avg. Rating
                </th>
              </tr>
            </thead>
            <tbody>
              {analytics.topAgents?.map((agent, index) => (
                <tr
                  key={agent.id}
                  className="border-b border-border last:border-0"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-text-muted">
                        {index + 1}
                      </span>
                      <span className="font-medium text-text-primary">
                        {agent.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-text-secondary">
                    {agent.owner || "-"}
                  </td>
                  <td className="py-3 px-4 text-text-secondary">
                    {agent.category}
                  </td>
                  <td className="py-3 px-4 text-right text-text-primary">
                    {formatNumber(agent.sessions)}
                  </td>
                  <td className="py-3 px-4 text-right text-text-primary">
                    {formatNumber(agent.messages)}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="inline-flex items-center gap-1 text-amber-500">
                      {agent.rating?.toFixed(1) || "-"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
